/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : Dialog전역저장소
 * 버전 : 1.0
 * 파일명 : DialogStore.ts
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================
 * 수정 : 2025-07-02, 강동형 : 저작권 추가, 더보기 버튼 추가에 따른 criterion 변수 추가
 * 수정 : 2025-07-16, 강동형 : 다이얼로그 컴포넌트 화에 따른 필드 추가, 미방문 기간 기본값 변경
 * 수정 : 2025-12-29, 강동형 : MainPage값 변경
 * ================================================================ 
 */

// dialogStore.ts
import { create } from 'zustand';
import { BottomSheetType, RegionsType } from 'src/types/SchoolType';

export interface DialogState {
  isOpen: boolean;
  months_last_visit: number | string;
  temp_months_last_visit: number;
  start_date:string | Date | null;
  temp_start_date:string | Date | null;
  end_date:string | Date | null;
  temp_end_date:string | Date | null;
  wide_loc: string;
  city_loc: string;
  edu_level: number[];
  temp_edu_level: number[];
  highschool_type: number[];
  temp_highschool_type: number[];
  special_note: number[];
  temp_special_note: number[];
  selected_Regions : RegionsType
  page_Num:number;
  response_data: any
  school_name:string,
  sort_by:string,
  sort_order:string,
  total_rows:number,
  locations: RegionsType,
  temp_locations: RegionsType,
  element : any
  loadBy: any
  elementValue : string
  setOpen: (value: boolean) => void;
  setResponse: (data: any, page?:number) => void;
  setField: (field: keyof DialogState, value: any) => void 
  resetField: (field: keyof DialogState) => void;
  setRegions: () => void
  setLocations: (regions: RegionsType) => void
  setTempLocations: (regions: RegionsType) => void
  toggleFilter: (category: "temp_edu_level" | "temp_highschool_type" | "temp_special_note", value: number, defaultValue: number) => void;
  syncField: (field:keyof DialogState,to: "temp" | "base", from: "temp" | "base") => void;
  reset: (open?: boolean) => void;
}

const today = new Date()
const oneMonthAgo = new Date();    
oneMonthAgo.setMonth(today.getMonth() - 1);

const defaultValue = {
  months_last_visit: 0,
  temp_months_last_visit: 0,
  start_date: '',
  temp_start_date: '',
  end_date: '',
  temp_end_date: '',
  wide_loc: '',
  city_loc: '',
  edu_level: [0],
  temp_edu_level: [0],
  highschool_type: [0],
  temp_highschool_type: [0],
  special_note: [0],
  temp_special_note: [0],
  selected_Regions : {},
  page_Num:1,
  response_data:'',
  school_name:'',
  sort_by:'school_name',
  sort_order:'asc',
  total_rows:0,
  locations: {},
  temp_locations:{},
  element: '',
  loadBy : '',
  elementValue : '',
}

export const useDialogStore = create<DialogState>((set) => ({
  isOpen: false,
  ...defaultValue,
  setOpen: (value: boolean) => set({ isOpen: value }),
  setResponse: (data: any[], page = 1) =>
  set((state) => {
    if (page === 1) {
      return {
        response_data: data,
      };
    }
    return {
      response_data: [
      ...(state.response_data ?? []),
      ...data,
    ],
    };
  }),
  setField: (field,value)=>set((state)=>({...state,[field]:value})),
  resetField: (field) => set((state) => ({...state,[field]: defaultValue[field as keyof typeof defaultValue]})),
  setRegions: () =>
    set((state) => ({
      selected_Regions: state.locations, // ✅ 현재 상태의 locations를 복사해서 selected_Regions로 설정
    })),
  setLocations:(newRegions: RegionsType) =>
    set(() => ({
      locations: newRegions,
    })),
  setTempLocations:(newRegions: RegionsType) =>
    set(() => ({
      temp_locations: newRegions,
    })),
  toggleFilter: (category, value, defaultValue) =>
    set((state) => {
      const currentValues = state[category];
      let updatedValues;

      if (value === defaultValue) {
        // ✅ "전체"를 누르면 기존 값 제거하고 "전체"만 남김
        updatedValues = [defaultValue];
      } else {
        // 우선 "전체"(기본값)를 제거한 후
      const filteredValues = currentValues.filter((v) => v !== defaultValue);

      // 이미 선택된 값이면 제거, 없으면 추가
      if (filteredValues.includes(value)) {
        updatedValues = filteredValues.filter((v) => v !== value);
        // 모든 값이 제거되면 기본값으로 복원
        if (updatedValues.length === 0) updatedValues = [defaultValue];
      } else {
        updatedValues = [...filteredValues, value];
      }
      }

      let newState = { ...state, [category]: updatedValues };

      // edu_level이 변경되면 고등학교 관련 필드 업데이트
    if (category === "temp_edu_level") {
      // 고등학교(1)가 선택되지 않은 경우 highschool_type을 기본값(0)으로 초기화
      if (!updatedValues.includes(1)) {
        newState.temp_highschool_type = [defaultValue];
      }
      // edu_level이 오직 고등학교(1)만 선택된 경우 special_note도 기본값으로 초기화
      if (updatedValues.length === 1 && updatedValues[0] === 1) {
        newState.temp_special_note = [defaultValue];
      }
    }

      return newState;
    }),
  syncField: (field: string, to: "temp" | "base", from: "temp" | "base") =>
    set((state) => {
      const fieldMap: Record<string, [keyof DialogState, keyof DialogState]> = {
        edu_level: ["temp_edu_level", "edu_level"],
        highschool_type: ["temp_highschool_type", "highschool_type"],
        special_note: ["temp_special_note", "special_note"],
      };

      const pair = fieldMap[field];
      if (!pair) {
        console.warn(`"${field}" is not a valid syncable field`);
        return {};
      }

      const [tempKey, baseKey] = pair;
      const toKey = to === "temp" ? tempKey : baseKey;
      const fromKey = from === "temp" ? tempKey : baseKey;

      return {
        [toKey]: state[fromKey],
      };
    }),
  reset: (open = true) => {
    // console.log('open', open); // 🔴 false 찍힘
    set((state) => ({
      isOpen: open,
      ...defaultValue,
      selected_Regions: open ? state.selected_Regions : {}, // ✅ 조건 분기
      total_rows: open ? state.total_rows : 0, // ✅ 조건 분기
    }));
  },
  }))


interface SchoolListDialogState{
  isOpen:boolean,
  selected : BottomSheetType | null,
  criterion: string,
  setField: (field: keyof SchoolListDialogState, value: any) => void 
  reset : (open?: boolean)=>void
}

const SchoolListDialogDefault = {
  isOpen:false,
  selected : null,
  criterion: ''
}

export const useSchoolListDialogStore = create<SchoolListDialogState>((set)=>({
  ...SchoolListDialogDefault,
  setField : (field,value)=>set((state)=>({...state,[field]:value})),
  reset: (open = false)=>
    set((state)=>({
      ...SchoolListDialogDefault,
      isOpen: open,
      criterion: open ? state.criterion : ''
    }))
}))

interface MainPageDialogState {
  isOpen:boolean,
  setField: (field: keyof MainPageDialogState, value: any) => void 
  selected : any,
  tempTextArea : string
  reset: (field?: keyof MainPageDialogState) => void;
}

const MainPageDialogDefault = {
  isOpen:false,
  selected : null,
  tempTextArea: ''
}

export const useMainPageDialogStore = create<MainPageDialogState>((set)=>({
  ...MainPageDialogDefault,
  setField : (field,value)=>set((state)=>({...state,[field]:value})),
  reset: (field) =>
      set((state) => {
          if (!field) {
              // 전체 초기화
              return { ...state, ...MainPageDialogDefault }
          }
          // 특정 필드만 초기화
          return { ...state, [field]: MainPageDialogDefault[field as keyof typeof MainPageDialogDefault] }
      }),
}))